// Centralized order-creation + Stripe redirect for the FINAL checkout step.
// Reads every selection from sessionStorage (customer info, insurance, add-ons)
// by key, so it works no matter the visual order of the steps. This lets the
// "Pay" action live on whichever step is last (currently the insurance step,
// after the add-ons-first reorder).
import { createSecureOrder, createSecurePaymentSession } from "./secureOrderService";

// Convert frontend add-on selections to the backend format.
const convertAddOnSelections = (selectedAddOns = {}, quantities = {}) => {
  const selections = {};
  Object.keys(selectedAddOns).forEach((addOnId) => {
    if (selectedAddOns[addOnId]) {
      selections[addOnId] = quantities[addOnId] || 1;
    }
  });
  return selections;
};

// Gathers booking data, creates the secure order (server prices it), opens the
// Stripe session and redirects the browser. Throws on validation / payment
// errors so the caller can show UI and reset its processing state.
export const submitBookingAndRedirect = async ({ t, showError, paymentOption = "deposit" }) => {
  const camperBooking = JSON.parse(sessionStorage.getItem("bookingData") || "{}");
  const step1Data = JSON.parse(sessionStorage.getItem("checkoutStep1Data") || "{}");
  const step2Data = JSON.parse(sessionStorage.getItem("checkoutStep2Data") || "{}");
  const step3Data = JSON.parse(sessionStorage.getItem("checkoutStep3Data") || "{}");

  // Validate critical booking data before proceeding
  if (!camperBooking.vehicleId) {
    throw new Error(t("step3.errors.vehicleNotSelected"));
  }
  if (!camperBooking.nights || !camperBooking.startDate || !camperBooking.endDate) {
    throw new Error(t("step3.errors.datesNotSelected"));
  }
  if (!step2Data.selectedPlan) {
    throw new Error("Please select an insurance plan.");
  }
  if (!step1Data.firstName || !step1Data.lastName || !step1Data.email || !step1Data.phone) {
    throw new Error("Required customer information is missing. Please complete your details.");
  }

  // SECURE: send selections only (no prices) — the backend calculates pricing.
  const secureOrderData = {
    vehicleId: parseInt(camperBooking.vehicleId),
    nights: parseInt(camperBooking.nights),
    startDate: camperBooking.startDate,
    endDate: camperBooking.endDate,
    pickupLocation: camperBooking.pickupLocation?.name || "",
    dropoffLocation: camperBooking.dropoffLocation?.name || "",
    pickupTime: camperBooking.pickupTime?.time || "",
    dropoffTime: camperBooking.dropoffTime?.time || "",
    adults: parseInt(step1Data.adults) || 1,
    children: parseInt(step1Data.children) || 0,
    pets: step1Data.pets || false,
    numberOfPets: parseInt(step1Data.numberOfPets) || 0,
    selectedInsurance: step2Data.selectedPlan || "",
    addOnSelections: convertAddOnSelections(step3Data.selectedAddOns || {}, step3Data.quantities || {}),
    customerInfo: {
      firstName: step1Data.firstName || "",
      lastName: step1Data.lastName || "",
      email: step1Data.email || "",
      phone: step1Data.phone || "",
      country: step1Data.country || "",
      licenseStartDate: step1Data.licenseStartDate || "",
      licenseExpiry: step1Data.licenseExpiry || "",
      vatNumber: step1Data.vatNumber || "",
    },
  };

  // Step 1: create secure order (server-validated pricing)
  const orderResponse = await createSecureOrder(secureOrderData);

  // Step 2: create the Stripe payment session for that order, charging either
  // the 50% deposit or the full amount depending on what the customer picked.
  const paymentResponse = await createSecurePaymentSession(orderResponse.orderId, paymentOption);

  const checkoutUrl =
    paymentResponse.url ||
    paymentResponse.sessionUrl ||
    paymentResponse.checkoutUrl ||
    paymentResponse.session_url ||
    paymentResponse.checkout_url;

  if (!checkoutUrl) {
    showError(
      `${t("step3.errors.paymentSessionFailed")} - Order ID: ${orderResponse.orderId}, Error: ${paymentResponse.error || "Unknown error"}`
    );
    throw new Error(paymentResponse.error || "No checkout URL received from payment session");
  }

  // Redirect to Stripe checkout
  window.location.href = checkoutUrl;
};
